import { Box, Card, Stack, Typography, alpha, useTheme } from '@mui/material';
import { ComponentType } from 'react';
import {
  MdAttachMoney,
  MdBusiness,
  MdCalendarMonth,
  MdCampaign,
  MdDashboard,
  MdErrorOutline,
  MdGroup,
  MdKeyboardArrowRight,
  MdLocalShipping,
  MdMenuBook,
  MdNotificationsNone,
  MdOutlineDescription,
  MdPersonOutline,
  MdRadioButtonUnchecked,
  MdTune,
} from 'react-icons/md';
import { ButtonIcon, CardFragment as CardData } from 'generated/graphql';
import ActionButton from './ActionButton';

type IconType = ComponentType<{ size?: number; color?: string }>;

const COMPACT_ICON_BY_BUTTON_ICON: Partial<Record<ButtonIcon, IconType>> = {
  [ButtonIcon.Tune]: MdTune,
  [ButtonIcon.Money]: MdAttachMoney,
  [ButtonIcon.Campaign]: MdCampaign,
  [ButtonIcon.Catalog]: MdMenuBook,
  [ButtonIcon.Group]: MdGroup,
  [ButtonIcon.Profile]: MdPersonOutline,
  [ButtonIcon.Business]: MdBusiness,
  [ButtonIcon.Bell]: MdNotificationsNone,
  [ButtonIcon.Document]: MdOutlineDescription,
  [ButtonIcon.Calendar]: MdCalendarMonth,
  [ButtonIcon.Truck]: MdLocalShipping,
  [ButtonIcon.Dashboard]: MdDashboard,
  [ButtonIcon.ErrorOutline]: MdErrorOutline,
};

const isDangerIcon = (icon: ButtonIcon) => icon === ButtonIcon.ErrorOutline;

export const MenuItemCard = ({
  data,
  onClick,
  disabled = false,
  compact = false,
  loading = false,
}: {
  data: CardData;
  onClick: () => void;
  disabled?: boolean;
  compact?: boolean;
  loading?: boolean;
}) => {
  const theme = useTheme();
  const isInteractionBlocked = disabled || loading;
  const showArrow = !compact && (!data.button.value || data.button.value === '');
  const cardColor = data.color?.toLowerCase();
  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        backgroundColor:
          cardColor === 'primary'
            ? alpha(theme.palette.primary.main, 0.2)
            : cardColor === 'secondary'
              ? alpha(theme.palette.secondary.main, 0.2)
              : '',
        opacity: isInteractionBlocked ? 0.6 : 0.75,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(0, 2),
        border: `1px solid ${theme.palette.grey[300]}`,
        '&:hover': {
          backgroundColor: !isInteractionBlocked && data.button.actions[0] ? theme.palette.grey[100] : '',
        },
        marginY: compact ? 0 : theme.spacing(1),
        height: compact ? 60 : 90,
        cursor: isInteractionBlocked ? 'default' : 'pointer',
        ...(disabled ? { pointerEvents: 'none' as const } : {}),
      })}
      onClick={(event) => {
        event.stopPropagation();
        if (!isInteractionBlocked) onClick();
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1.5,
          minWidth: 0,
        }}
      >
        {compact &&
          (() => {
            const IconComp = COMPACT_ICON_BY_BUTTON_ICON[data.icon] ?? MdRadioButtonUnchecked;
            const isDanger = isDangerIcon(data.icon);
            const iconColor = isDanger ? theme.palette.error.main : theme.palette.primary.main;
            return (
              <Box
                sx={{
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(iconColor, 0.12),
                  color: iconColor,
                }}
              >
                <IconComp size={20} color={iconColor} />
              </Box>
            );
          })()}
        <Stack direction="column" sx={{ padding: 0, minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={(theme) => ({
              color: cardColor === 'error' ? theme.palette.error.main : theme.palette.text.primary,
              fontWeight: theme.typography.fontWeightBold,
            })}
          >
            {data.title}
          </Typography>
          {!compact && (
            <Typography
              variant="body1"
              sx={(theme) => ({ fontWeight: theme.typography.fontWeightRegular, color: theme.palette.grey[800] })}
            >
              {data.description}
            </Typography>
          )}
        </Stack>
      </Stack>
      {showArrow ? (
        <MdKeyboardArrowRight size={20} />
      ) : !compact && data.button.value ? (
        <ActionButton
          {...data.button}
          size="medium"
          disabled={disabled || !data.button.actions[0] || data.button.disabled}
          loading={loading}
          onClick={onClick}
        />
      ) : null}
    </Card>
  );
};

export default MenuItemCard;
